import heic2any from 'heic2any'
import { api } from './api'
import { removeBackgroundBody } from './removeBackgroundBody'
export { browserDecodesWebp, removeBackgroundBody } from './removeBackgroundBody'
import { findContentBounds, boundsWithMargin } from './imageBounds'
import { findStraightenRegion, straightenLayout } from './imageStraighten'

// Constants
const TARGET_RATIO = 4 / 5
// Breathing room left around the product after trimming, as a fraction of its long edge.
const SUBJECT_MARGIN = 0.04
const BRIGHTNESS = '105%'
const CONTRAST = '110%'
const OUTPUT_FORMAT = 'image/jpeg'
const OUTPUT_QUALITY = 0.92
/** Long edge of the thumbnail used to tell a photo, a padded photo and a cutout apart. */
const REGION_SAMPLE_SIZE = 512
/** Straightening a level image: the whole frame, no turn to hide. */
const LEVEL_REGION = { x0: 0, y0: 0, x1: 1, y1: 1, zoom: false }

// Upload validation constants
export const ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'image/webp']
export const MAX_FILE_SIZE_MB = 20
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
export const MIN_DIMENSION = 800
const BLUR_THRESHOLD = 50
const UNDEREXPOSED_THRESHOLD = 50
const OVEREXPOSED_THRESHOLD = 220

/**
 * Apply brightness/contrast colour correction and 4:5 aspect ratio with white padding.
 * @param {string} base64ImageData - Raw base64 image data (no data: prefix)
 * @param {string} contentType - MIME type of the source image
 * @returns {Promise<string>} Processed base64 image data
 */
export function applyColourAndRatio(base64ImageData, contentType) {
  return renderProcessed(base64ImageData, contentType, (canvas, resolve) => {
    resolve(canvas.toDataURL(OUTPUT_FORMAT, OUTPUT_QUALITY).split(',')[1])
  })
}

/**
 * Same as applyColourAndRatio but returns a Blob instead of base64.
 * Much more efficient for uploading (no base64 encoding overhead).
 */
export function applyColourAndRatioBlob(base64ImageData, contentType) {
  return renderProcessed(base64ImageData, contentType, (canvas, resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Failed to create image blob')),
      OUTPUT_FORMAT,
      OUTPUT_QUALITY
    )
  })
}

/**
 * Remove background from an image via the remove-background API.
 * @param {string} imageUrl - Public URL of the image to process
 * @returns {Promise<{image_data: string, content_type: string}>}
 */
export async function removeBackground(imageUrl) {
  const response = await api.post('/api/image/remove-background', await removeBackgroundBody(imageUrl))

  if (!response.success || !response.image_data) {
    throw new Error('Background removal failed')
  }

  return {
    image_data: response.image_data,
    content_type: response.content_type || OUTPUT_FORMAT
  }
}

/**
 * Fetch an image URL and convert it to base64.
 * @param {string} url - Image URL to fetch
 * @returns {Promise<{base64: string, contentType: string}>}
 */
export async function urlToBase64(url) {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`)
  }

  const blob = await response.blob()
  const contentType = blob.type || OUTPUT_FORMAT

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]
      resolve({ base64, contentType })
    }
    reader.onerror = () => reject(new Error('Failed to convert image to base64'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Validate a file before upload. Returns null if valid, or an error message string.
 * Checks format, file size, and dimensions.
 * @param {File} file
 * @returns {Promise<string|null>} Error message or null if valid
 */
export function validateImageFile(file) {
  return new Promise((resolve) => {
    if (!ALLOWED_FORMATS.includes(file.type)) {
      resolve(`"${file.name}" — unsupported format. Use JPEG, PNG, or WebP.`)
      return
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      resolve(`"${file.name}" — file too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum is ${MAX_FILE_SIZE_MB}MB.`)
      return
    }

    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      if (image.width < MIN_DIMENSION || image.height < MIN_DIMENSION) {
        resolve(`"${file.name}" — too small (${image.width}×${image.height}). Minimum is ${MIN_DIMENSION}×${MIN_DIMENSION}px.`)
        return
      }
      resolve(null)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(`"${file.name}" — could not read image.`)
    }
    image.src = url
  })
}

/**
 * Convert a HEIC/HEIF image file to a JPEG File for browser upload.
 * Non-HEIC files are returned unchanged.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function convertHeicToJpeg(file) {
  const type = (file?.type || '').toLowerCase()
  const name = file?.name || 'upload'
  const isHeic = type === 'image/heic' || type === 'image/heif' || /\.(heic|heif)$/i.test(name)
  if (!isHeic) return file

  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.9
  })

  const jpegBlob = Array.isArray(converted) ? converted[0] : converted
  if (!(jpegBlob instanceof Blob)) {
    throw new Error(`Failed to convert "${name}" to JPEG`)
  }

  const jpegName = name.replace(/\.(heic|heif)$/i, '.jpg')
  return new File([jpegBlob], jpegName, {
    type: 'image/jpeg',
    lastModified: Date.now()
  })
}

/**
 * Run visual quality checks on a file (blur + exposure).
 * Returns null if acceptable, or an error message string.
 * @param {File} file
 * @returns {Promise<string|null>} Error message or null if acceptable
 */
export function checkImageQuality(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)

      // Downscale for analysis preserving aspect ratio
      const maxAnalysisSize = 256
      const scale = Math.min(maxAnalysisSize / image.width, maxAnalysisSize / image.height)
      const analysisWidth = Math.round(image.width * scale)
      const analysisHeight = Math.round(image.height * scale)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = analysisWidth
      canvas.height = analysisHeight
      ctx.drawImage(image, 0, 0, analysisWidth, analysisHeight)
      const imageData = ctx.getImageData(0, 0, analysisWidth, analysisHeight)

      const blurScore = computeLaplacianVariance(imageData)
      if (blurScore < BLUR_THRESHOLD) {
        resolve(`"${file.name}" — photo appears blurry. Please retake with better focus.`)
        return
      }

      const avgBrightness = computeAverageBrightness(imageData)
      if (avgBrightness < UNDEREXPOSED_THRESHOLD) {
        resolve(`"${file.name}" — photo is too dark (underexposed). Please retake with better lighting.`)
        return
      }
      if (avgBrightness > OVEREXPOSED_THRESHOLD) {
        resolve(`"${file.name}" — photo is too bright (overexposed). Please retake with less light.`)
        return
      }

      resolve(null)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(`"${file.name}" — could not analyse image quality.`)
    }
    image.src = url
  })
}

/** Load an image in a way that leaves the canvas readable (CORS-clean). */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Failed to load image'))
    image.src = src
  })
}

/**
 * Which part of the image turns when it is straightened (see imageStraighten.js).
 * Read from a thumbnail: a full-size read of a camera photo costs tens of megabytes.
 */
export function readStraightenRegion(image) {
  const scale = Math.min(1, REGION_SAMPLE_SIZE / Math.max(image.width, image.height))
  const scratch = document.createElement('canvas')
  scratch.width = Math.max(1, Math.round(image.width * scale))
  scratch.height = Math.max(1, Math.round(image.height * scale))
  // Deliberately NOT white-filled: transparent corners are the cutout signal.
  const ctx = scratch.getContext('2d', { willReadFrequently: true })
  ctx.drawImage(image, 0, 0, scratch.width, scratch.height)
  return findStraightenRegion(ctx.getImageData(0, 0, scratch.width, scratch.height))
}

/**
 * Draw the image straightened inside its own frame, on white, with a colour filter.
 * The canvas keeps the image's width and height. See imageStraighten.js for which
 * part turns and whether it zooms. At 0 degrees this is a plain filtered copy.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} image
 * @param {{degrees?: number, filter?: string}} options
 */
export function straightenToCanvas(image, { degrees = 0, filter = 'none' } = {}) {
  const { width, height } = image
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, width, height)

  const region = degrees === 0 ? LEVEL_REGION : readStraightenRegion(image)
  const { box, scale } = straightenLayout({ width, height, degrees, region })
  const centreX = box.x + box.width / 2
  const centreY = box.y + box.height / 2

  ctx.beginPath()
  ctx.rect(box.x, box.y, box.width, box.height)
  ctx.clip()
  ctx.filter = filter
  ctx.translate(centreX, centreY)
  ctx.rotate((degrees * Math.PI) / 180)
  ctx.scale(scale, scale)
  ctx.drawImage(image, -centreX, -centreY)
  return canvas
}

/**
 * Turn the image by whole quarter turns: 1 is 90 degrees clockwise, -1 is anticlockwise.
 * Nothing is cropped or scaled; width and height swap on an odd turn. The canvas is not
 * filled, so a transparent cutout stays transparent and straightening still sees it.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} image
 * @param {number} turns
 */
export function rotateQuarterTurns(image, turns) {
  const quarter = ((turns % 4) + 4) % 4
  const { width, height } = image
  const sideways = quarter % 2 === 1
  const canvas = document.createElement('canvas')
  canvas.width = sideways ? height : width
  canvas.height = sideways ? width : height
  const ctx = canvas.getContext('2d')
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((quarter * Math.PI) / 2)
  ctx.drawImage(image, -width / 2, -height / 2)
  return canvas
}

/** Centre the source on a white 4:5 canvas, adding whitespace and never scaling. */
export function padToPortrait(source) {
  const { canvas, ctx } = createPaddedCanvas(source)
  ctx.drawImage(source, Math.round((canvas.width - source.width) / 2), Math.round((canvas.height - source.height) / 2))
  return canvas
}

// --- Internal helpers ---

/** Shopify never needs more than this; capping happens after drawing, never before. */
const MAX_DIMENSION = 2048

/**
 * Load, trim, colour-correct, pad to 4:5 and cap the size, then hand the finished
 * canvas to `emit`. Both public entry points share this so the pipeline exists once.
 */
function renderProcessed(base64ImageData, contentType, emit) {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'

    image.onload = () => {
      try {
        const subject = trimToSubject(image)
        const { canvas, ctx } = createPaddedCanvas(subject)
        const offsetX = Math.round((canvas.width - subject.width) / 2)
        const offsetY = Math.round((canvas.height - subject.height) / 2)

        ctx.filter = `brightness(${BRIGHTNESS}) contrast(${CONTRAST})`
        ctx.drawImage(subject, offsetX, offsetY)

        emit(downscaleIfNeeded(canvas), resolve, reject)
      } catch (error) {
        reject(error)
      }
    }

    image.onerror = () => reject(new Error('Failed to load image for processing'))
    image.src = `data:${contentType};base64,${base64ImageData}`
  })
}

/**
 * Crop away the blank border around the product, leaving SUBJECT_MARGIN breathing room.
 *
 * Background removal returns the subject on a blank field but keeps the photographer's
 * framing, and the 4:5 pad below then adds more blank space on top. Without this step a
 * typical product occupied ~62% of its own frame and storefront cards rendered a stamp
 * on a white field (measured across 530 live products, 2026-08-25).
 *
 * Returns the original image untouched when there is nothing to trim, so a blank or
 * edge-to-edge photo is never made worse. The scratch canvas is deliberately NOT
 * white-filled: the alpha channel is what locates the subject on a removed background.
 */
function trimToSubject(image) {
  const scratch = document.createElement('canvas')
  scratch.width = image.width
  scratch.height = image.height
  const scratchCtx = scratch.getContext('2d', { willReadFrequently: true })
  scratchCtx.drawImage(image, 0, 0)

  const pixels = scratchCtx.getImageData(0, 0, scratch.width, scratch.height)
  const bounds = findContentBounds(pixels)
  if (!bounds) return image

  const box = boundsWithMargin(bounds, scratch, SUBJECT_MARGIN)
  const width = box.right - box.left + 1
  const height = box.bottom - box.top + 1
  if (width >= image.width && height >= image.height) return image

  const cropped = document.createElement('canvas')
  cropped.width = width
  cropped.height = height
  cropped.getContext('2d').drawImage(scratch, box.left, box.top, width, height, 0, 0, width, height)
  return cropped
}

function downscaleIfNeeded(canvas) {
  if (canvas.width <= MAX_DIMENSION && canvas.height <= MAX_DIMENSION) return canvas
  const scale = MAX_DIMENSION / Math.max(canvas.width, canvas.height)
  const small = document.createElement('canvas')
  small.width = Math.round(canvas.width * scale)
  small.height = Math.round(canvas.height * scale)
  const ctx = small.getContext('2d')
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, small.width, small.height)
  ctx.drawImage(canvas, 0, 0, small.width, small.height)
  return small
}

/** White-filled canvas at 4:5, sized so the source sits inside it without scaling. */
function createPaddedCanvas(image) {
  const currentRatio = image.width / image.height
  let newWidth, newHeight

  if (currentRatio > TARGET_RATIO) {
    newWidth = image.width
    newHeight = Math.round(image.width / TARGET_RATIO)
  } else if (currentRatio < TARGET_RATIO) {
    newHeight = image.height
    newWidth = Math.round(image.height * TARGET_RATIO)
  } else {
    newWidth = image.width
    newHeight = image.height
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  canvas.width = newWidth
  canvas.height = newHeight
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, newWidth, newHeight)

  return { canvas, ctx }
}

/**
 * Compute Laplacian variance as a blur metric.
 * Higher values = sharper image. Low values = blurry.
 */
function computeLaplacianVariance(imageData) {
  const { data, width, height } = imageData
  const grey = new Float32Array(width * height)

  // Convert to greyscale
  for (let i = 0; i < grey.length; i++) {
    const offset = i * 4
    grey[i] = 0.299 * data[offset] + 0.587 * data[offset + 1] + 0.114 * data[offset + 2]
  }

  // Apply 3×3 Laplacian kernel: [0,1,0; 1,-4,1; 0,1,0]
  let sum = 0
  let sumSq = 0
  let count = 0

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x
      const laplacian =
        grey[idx - width] +
        grey[idx - 1] +
        (-4 * grey[idx]) +
        grey[idx + 1] +
        grey[idx + width]
      sum += laplacian
      sumSq += laplacian * laplacian
      count++
    }
  }

  const mean = sum / count
  return (sumSq / count) - (mean * mean)
}

/**
 * Compute average brightness (0–255) across all pixels.
 */
function computeAverageBrightness(imageData) {
  const { data } = imageData
  let total = 0
  const pixelCount = data.length / 4

  for (let i = 0; i < data.length; i += 4) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
  }

  return total / pixelCount
}
